import { PresenceUser } from '@/types/presence';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';

interface EditorPresenceProps {
  users: PresenceUser[];
  maxVisible?: number;
}

export function EditorPresence({ users, maxVisible = 3 }: EditorPresenceProps) {
  // Filter only users in editor mode
  const editorUsers = users.filter((u) => u.location === 'editor');
  const visibleUsers = editorUsers.slice(0, maxVisible);
  const remainingCount = Math.max(0, editorUsers.length - maxVisible);

  if (editorUsers.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center -space-x-1.5">
        {visibleUsers.map((user) => (
          <UserAvatar key={user.userId} user={user} />
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-xs font-medium text-gray-600 ring-1 ring-gray-300">
                +{remainingCount}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                {editorUsers.slice(maxVisible).map((user) => (
                  <div key={user.userId} className="text-sm">
                    {user.name}
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

interface UserAvatarProps {
  user: PresenceUser;
}

function UserAvatar({ user }: UserAvatarProps) {
  const initials = getInitials(user.name);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-white transition-transform hover:z-10 hover:scale-110"
          style={{
            backgroundColor: user.avatar ? 'transparent' : user.color,
            boxShadow: `0 0 0 2px ${user.color}40`,
          }}
        >
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <span className="text-xs font-semibold text-white">{initials}</span>
          )}

          {/* Active indicator with pulse animation */}
          <div className="absolute -right-0.5 -bottom-0.5">
            <div className="relative">
              <div className="h-2 w-2 rounded-full border border-white bg-green-500" />
              <div className="absolute inset-0 h-2 w-2 animate-ping rounded-full bg-green-400 opacity-75" />
            </div>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          <div className="font-medium">{user.name}</div>
          <div className="text-xs text-gray-400">Currently editing</div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}
